import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { atualizarJson, enviarJson, pedirJson } from "./api";
import type { EstadoDoChip } from "../componentes/ui/Chip";

/* Sucesso do cliente: a saúde de cada organização, a carteira de CS e os alertas de queda de
   faixa. O contrato é o de `api/app/dominios/sucesso/`. Tudo é só da equipe: o cliente nunca vê
   a própria nota, e por isso nenhuma destas chamadas existe na área do cliente. */

export const CHAVE_DO_SUCESSO = ["sucesso"] as const;

const uuid = z.string().uuid();

export const FAIXAS = ["saudavel", "atencao", "risco"] as const;
export type Faixa = (typeof FAIXAS)[number];

export const ROTULO_DA_FAIXA: Record<Faixa, string> = {
  saudavel: "Saudável",
  atencao: "Atenção",
  risco: "Risco",
};

export const CHIP_DA_FAIXA: Record<Faixa, EstadoDoChip> = {
  saudavel: "ok",
  atencao: "atencao",
  risco: "erro",
};

export const ORDENS = ["risco", "queda", "nome"] as const;
export type OrdemDaCarteira = (typeof ORDENS)[number];

export const ROTULO_DA_ORDEM: Record<OrdemDaCarteira, string> = {
  risco: "Maior risco primeiro",
  queda: "Maior queda na semana",
  nome: "Nome",
};

export const SINAIS = [
  "chamados_abertos",
  "reabertos",
  "sla",
  "satisfacao",
  "contato",
  "implantacao",
] as const;
export type Sinal = (typeof SINAIS)[number];

export const ROTULO_DO_SINAL: Record<Sinal, string> = {
  chamados_abertos: "Chamados abertos",
  reabertos: "Chamados reabertos",
  sla: "Prazos de atendimento (SLA)",
  satisfacao: "Satisfação do cliente",
  contato: "Tempo desde o último contato",
  implantacao: "Implantação",
};

const esquemaPessoa = z.object({ id: uuid, nome: z.string() });
const esquemaOrganizacao = z.object({ id: uuid, nome: z.string() });
const esquemaFaixa = z.enum(FAIXAS);

const esquemaLinha = z.object({
  organizacao: esquemaOrganizacao,
  classificacao: z.string().nullable(),
  score: z.number().int().nullable(),
  faixa: esquemaFaixa.nullable(),
  dia: z.string().nullable(),
  variacao_7_dias: z.number().int().nullable(),
  responsavel: esquemaPessoa.nullable(),
});

const esquemaSinal = z.object({
  peso: z.number().int(),
  nota: z.number().nullable(),
  entra_no_score: z.boolean(),
  detalhe: z.record(z.string(), z.unknown()),
});

const esquemaAlerta = z.object({
  id: uuid,
  organizacao: esquemaOrganizacao,
  dia: z.string(),
  de_faixa: esquemaFaixa,
  para_faixa: esquemaFaixa,
  score_anterior: z.number().int(),
  score: z.number().int(),
  responsavel: esquemaPessoa.nullable(),
  gerado_em: z.string(),
});

const esquemaSaudeDaOrganizacao = z.object({
  organizacao: esquemaOrganizacao,
  classificacao: z.string().nullable(),
  responsavel: esquemaPessoa.nullable(),
  score: z.number().int().nullable(),
  faixa: esquemaFaixa.nullable(),
  dia: z.string().nullable(),
  sinais: z.record(z.string(), esquemaSinal),
  evolucao: z.array(z.object({ dia: z.string(), score: z.number().int(), faixa: esquemaFaixa })),
  alertas: z.array(esquemaAlerta),
});

const esquemaConfiguracao = z.object({
  peso_chamados_abertos: z.number().int(),
  peso_reabertos: z.number().int(),
  peso_sla: z.number().int(),
  peso_satisfacao: z.number().int(),
  peso_contato: z.number().int(),
  peso_implantacao: z.number().int(),
  limite_saudavel: z.number().int(),
  limite_atencao: z.number().int(),
  atualizada_em: z.string(),
  atualizada_por_nome: z.string().nullable(),
});

const esquemaRecalculo = z.object({ organizacoes: z.number().int(), alertas: z.number().int() });

export type LinhaDaCarteira = z.infer<typeof esquemaLinha>;
export type SinalDaSaude = z.infer<typeof esquemaSinal>;
export type AlertaDeSaude = z.infer<typeof esquemaAlerta>;
export type SaudeDaOrganizacao = z.infer<typeof esquemaSaudeDaOrganizacao>;
export type ConfiguracaoDaSaude = z.infer<typeof esquemaConfiguracao>;
export type PessoaDaEquipe = z.infer<typeof esquemaPessoa>;

/* ------------------------------------------------------------------------------- frases */

function plural(n: number, singular: string, pluralDaPalavra: string): string {
  return `${n} ${n === 1 ? singular : pluralDaPalavra}`;
}

function inteiro(valor: unknown): number {
  return typeof valor === "number" ? valor : 0;
}

/** O fato que explica a nota de cada sinal, em português direto. */
export function descreverSinal(sinal: Sinal, detalhe: Record<string, unknown>): string {
  switch (sinal) {
    case "chamados_abertos":
      return inteiro(detalhe.abertos) === 0
        ? "Nenhum chamado aberto"
        : plural(inteiro(detalhe.abertos), "chamado aberto", "chamados abertos");
    case "reabertos":
      return inteiro(detalhe.reabertos) === 0
        ? "Nenhum chamado reaberto nos últimos 90 dias"
        : `${plural(inteiro(detalhe.reabertos), "chamado reaberto", "chamados reabertos")} nos últimos 90 dias`;
    case "sla": {
      const total = inteiro(detalhe.total);
      if (total === 0) return "Nenhum chamado com prazo de SLA nos últimos 90 dias";
      return `${inteiro(detalhe.violados)} de ${plural(total, "chamado", "chamados")} com prazo vencido (últimos 90 dias)`;
    }
    case "satisfacao":
      return inteiro(detalhe.respostas) === 0
        ? "Nenhuma resposta de satisfação nos últimos 180 dias"
        : `${plural(inteiro(detalhe.respostas), "resposta", "respostas")} nos últimos 180 dias`;
    case "contato":
      return `${plural(inteiro(detalhe.dias_sem_contato), "dia", "dias")} sem contato`;
    case "implantacao": {
      const atraso = detalhe.atraso_em_dias;
      if (atraso === null || atraso === undefined) return "Nenhuma implantação em andamento";
      return inteiro(atraso) === 0
        ? "Implantação dentro do prazo"
        : `${plural(inteiro(atraso), "dia", "dias")} de atraso na implantação`;
    }
  }
}

/** "+5", "-12" ou "sem base" — a variação do score nos últimos 7 dias. */
export function variacaoLegivel(variacao: number | null): string {
  if (variacao === null) return "sem base";
  if (variacao === 0) return "estável";
  return variacao > 0 ? `+${variacao}` : `${variacao}`;
}

export function quedaDeFaixa(alerta: Pick<AlertaDeSaude, "de_faixa" | "para_faixa">): string {
  return `${ROTULO_DA_FAIXA[alerta.de_faixa]} para ${ROTULO_DA_FAIXA[alerta.para_faixa]}`;
}

/* ------------------------------------------------------------------------ configuração */

export interface FormularioDaConfiguracao {
  peso_chamados_abertos: string;
  peso_reabertos: string;
  peso_sla: string;
  peso_satisfacao: string;
  peso_contato: string;
  peso_implantacao: string;
  limite_saudavel: string;
  limite_atencao: string;
}

export const CAMPOS_DE_PESO: ReadonlyArray<{ campo: keyof FormularioDaConfiguracao; sinal: Sinal }> = [
  { campo: "peso_chamados_abertos", sinal: "chamados_abertos" },
  { campo: "peso_reabertos", sinal: "reabertos" },
  { campo: "peso_sla", sinal: "sla" },
  { campo: "peso_satisfacao", sinal: "satisfacao" },
  { campo: "peso_contato", sinal: "contato" },
  { campo: "peso_implantacao", sinal: "implantacao" },
];

export function formularioDaConfiguracao(c: ConfiguracaoDaSaude): FormularioDaConfiguracao {
  return {
    peso_chamados_abertos: String(c.peso_chamados_abertos),
    peso_reabertos: String(c.peso_reabertos),
    peso_sla: String(c.peso_sla),
    peso_satisfacao: String(c.peso_satisfacao),
    peso_contato: String(c.peso_contato),
    peso_implantacao: String(c.peso_implantacao),
    limite_saudavel: String(c.limite_saudavel),
    limite_atencao: String(c.limite_atencao),
  };
}

function numeroInteiro(texto: string): number | null {
  return /^\d+$/.test(texto.trim()) ? Number(texto.trim()) : null;
}

/** As mesmas regras da API, para a tela barrar antes de ir ao servidor. */
export function validarConfiguracao(form: FormularioDaConfiguracao): string | null {
  const pesos = CAMPOS_DE_PESO.map(({ campo }) => numeroInteiro(form[campo]));
  if (pesos.some((p) => p === null || p > 100)) {
    return "Cada peso é um número inteiro de 0 a 100.";
  }
  if (pesos.reduce<number>((soma, p) => soma + (p ?? 0), 0) === 0) {
    return "Pelo menos um sinal precisa ter peso maior que zero.";
  }
  const saudavel = numeroInteiro(form.limite_saudavel);
  const atencao = numeroInteiro(form.limite_atencao);
  if (saudavel === null || atencao === null || saudavel > 100 || atencao < 1) {
    return "Os cortes são números inteiros: saudável até 100 e atenção a partir de 1.";
  }
  if (atencao >= saudavel) return "O corte de atenção precisa ficar abaixo do corte de saudável.";
  return null;
}

export function corpoDaConfiguracao(form: FormularioDaConfiguracao): Record<string, number> {
  return Object.fromEntries(Object.entries(form).map(([campo, valor]) => [campo, Number(valor)]));
}

/* ----------------------------------------------------------------------------- consultas */

export interface FiltrosDaCarteira {
  faixa?: Faixa | "";
  responsavelId?: string;
  semResponsavel?: boolean;
  busca?: string;
  ordem?: OrdemDaCarteira;
}

export function useCarteiraDeSaude(filtros: FiltrosDaCarteira = {}, habilitado = true) {
  const parametros = new URLSearchParams();
  if (filtros.faixa) parametros.set("faixa", filtros.faixa);
  if (filtros.responsavelId) parametros.set("responsavel_id", filtros.responsavelId);
  if (filtros.semResponsavel) parametros.set("sem_responsavel", "true");
  if (filtros.busca?.trim()) parametros.set("busca", filtros.busca.trim());
  parametros.set("ordem", filtros.ordem ?? "risco");
  const consulta = parametros.toString();
  return useQuery({
    queryKey: [...CHAVE_DO_SUCESSO, "carteira", consulta],
    enabled: habilitado,
    queryFn: ({ signal }) =>
      pedirJson(`/sucesso/saude?${consulta}`, z.array(esquemaLinha), { signal }),
  });
}

export function useSaudeDaOrganizacao(organizacaoId: string, habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_DO_SUCESSO, "organizacao", organizacaoId],
    enabled: habilitado,
    queryFn: ({ signal }) =>
      pedirJson(
        `/sucesso/saude/${encodeURIComponent(organizacaoId)}`,
        esquemaSaudeDaOrganizacao,
        { signal },
      ),
  });
}

export function useAlertasDeSaude(habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_DO_SUCESSO, "alertas"],
    enabled: habilitado,
    queryFn: ({ signal }) => pedirJson("/sucesso/alertas", z.array(esquemaAlerta), { signal }),
  });
}

export function useResponsaveisDeCS(habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_DO_SUCESSO, "responsaveis"],
    enabled: habilitado,
    queryFn: ({ signal }) => pedirJson("/sucesso/responsaveis", z.array(esquemaPessoa), { signal }),
  });
}

export function useConfiguracaoDaSaude(habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_DO_SUCESSO, "configuracao"],
    enabled: habilitado,
    queryFn: ({ signal }) => pedirJson("/sucesso/configuracao", esquemaConfiguracao, { signal }),
  });
}

export function useRecalcularSaude() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (organizacaoId: string | null) =>
      enviarJson("/sucesso/saude/recalculo", esquemaRecalculo, { organizacao_id: organizacaoId }),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DO_SUCESSO }),
  });
}

export function useDefinirResponsavelDeCS(organizacaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (responsavelId: string | null) =>
      atualizarJson(
        `/sucesso/saude/${encodeURIComponent(organizacaoId)}/responsavel`,
        esquemaSaudeDaOrganizacao,
        { responsavel_id: responsavelId },
      ),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DO_SUCESSO }),
  });
}

export function useSalvarConfiguracaoDaSaude() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: Record<string, number>) =>
      atualizarJson("/sucesso/configuracao", esquemaConfiguracao, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DO_SUCESSO }),
  });
}
